/**
 * PinnedMessages.jsx
 *
 * Exports:
 *   PinPanel          — slide-in drawer showing all pins, grouped by category
 *   PinPopover        — small category-picker popover rendered from message action bars
 *   usePinButton      — returns { isPinned, togglePin } for a given message + context
 */

import { useState, useEffect, useLayoutEffect, useRef, useCallback, useContext } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from './supabaseClient.js';
import { ThemeContext } from './ThemeContext.jsx';
import { maskSpoilersForSnippet } from './spoilers.jsx';

// ─── CATEGORY COLOR PALETTE ──────────────────────────────────────────────────
const CAT_COLORS = [
  '#d1b3ff', '#7aa2f7', '#9ece6a', '#f7768e', '#ff9e64',
  '#c4a882', '#73daca', '#e0af68', '#bb9af7', '#7dcfff',
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'yesterday';
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatPinDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function truncate(text, max = 140) {
  if (!text) return '—';
  // Mask ||spoilers|| BEFORE slicing — a pin card must not leak what the
  // message hides, and slicing first could cut a closing bar off.
  const masked = maskSpoilersForSnippet(text);
  return masked.length > max ? masked.slice(0, max) + '…' : masked;
}

// ─── PIN PANEL ────────────────────────────────────────────────────────────────
export function PinPanel({ contextType, contextId, userId, onClose, onJumpToMessage, t: tProp }) {
  const { theme: tCtx } = useContext(ThemeContext);
  const t = tProp || tCtx;

  const [pins, setPins] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({}); // { catId: bool }
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatName, setEditingCatName] = useState('');
  const [creatingCat, setCreatingCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState(CAT_COLORS[0]);
  const [removingPinId, setRemovingPinId] = useState(null);

  // ── Load data ──────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!contextId || !userId) return;
    setLoading(true);

    const contextCol = contextType === 'convo' ? 'convo_id'
      : contextType === 'channel' ? 'channel_id'
      : 'story_id';

    const [pinsRes, catsRes] = await Promise.all([
      supabase
        .from('pinned_messages')
        .select('*')
        .eq('pinned_by', userId)
        .eq(contextCol, contextId)
        .order('pinned_at', { ascending: false }),
      supabase
        .from('pin_categories')
        .select('*')
        .eq('created_by', userId)
        .order('sort_order', { ascending: true }),
    ]);

    setPins(pinsRes.data || []);
    setCategories(catsRes.data || []);
    setLoading(false);
  }, [contextId, contextType, userId]);

  useEffect(() => { load(); }, [load]);

  // ── Category CRUD ──────────────────────────────────────────────────────────
  const createCategory = async () => {
    const name = newCatName.trim();
    if (!name) return;
    const { data } = await supabase
      .from('pin_categories')
      .insert({ created_by: userId, name, color: newCatColor, sort_order: categories.length })
      .select()
      .single();
    if (data) {
      setCategories(prev => [...prev, data]);
      setNewCatName('');
      setCreatingCat(false);
    }
  };

  // AUDIT SW-10: every mutation here was a bare await followed by an
  // unconditional state change. Under owner-scoped RLS a stale session matches
  // 0 rows and reports success, so a batch of organizing looked done and was
  // back exactly as before on the next open, with no clue which ops landed.
  // Each write now verifies, and on failure resyncs from the DB and says so.
  const [pinError, setPinError] = useState('');
  const failed = (what, err) => {
    setPinError(`Couldn't ${what}${err ? `: ${String(err.message || err).slice(0, 70)}` : ' — nothing changed'}. Reloaded from the server.`);
    load();
  };

  const renameCategory = async (id) => {
    const name = editingCatName.trim();
    if (!name) return;
    const { data, error } = await supabase.from('pin_categories').update({ name }).eq('id', id).select('id');
    if (error || !data?.length) return failed('rename that category', error);
    setCategories(prev => prev.map(c => c.id === id ? { ...c, name } : c));
    setEditingCatId(null);
  };

  const deleteCategory = async (id) => {
    // Pins in this category stay but become uncategorized (category_id → null via DB ON DELETE SET NULL)
    const { data, error } = await supabase.from('pin_categories').delete().eq('id', id).select('id');
    if (error || !data?.length) return failed('delete that category', error);
    setCategories(prev => prev.filter(c => c.id !== id));
    setPins(prev => prev.map(p => p.category_id === id ? { ...p, category_id: null } : p));
  };

  const moveToCategory = async (pinId, catId) => {
    const { data, error } = await supabase.from('pinned_messages').update({ category_id: catId }).eq('id', pinId).select('id');
    if (error || !data?.length) return failed('move that pin', error);
    setPins(prev => prev.map(p => p.id === pinId ? { ...p, category_id: catId } : p));
  };

  // ── Remove pin ─────────────────────────────────────────────────────────────
  const removePin = async (pinId) => {
    const { data, error } = await supabase.from('pinned_messages').delete().eq('id', pinId).select('id');
    if (error || !data?.length) { setRemovingPinId(null); return failed('unpin that message', error); }
    setPins(prev => prev.filter(p => p.id !== pinId));
    setRemovingPinId(null);
  };

  // ── Group pins by category ─────────────────────────────────────────────────
  const pinsByCategory = {};
  const uncategorized = [];
  pins.forEach(p => {
    if (p.category_id) {
      if (!pinsByCategory[p.category_id]) pinsByCategory[p.category_id] = [];
      pinsByCategory[p.category_id].push(p);
    } else {
      uncategorized.push(p);
    }
  });

  const panelStyle = {
    position: 'fixed', top: 0, right: 0, bottom: 0,
    width: '320px', maxWidth: '92vw',
    background: 'var(--ds-glass, rgba(29, 16, 19, 0.77))',
    borderLeft: '1px solid var(--ds-glass-edge, #c4a8823d)',
    display: 'flex', flexDirection: 'column',
    zIndex: 300, boxShadow: '-4px 0 24px rgba(0,0,0,0.6), inset 1px 0 0 var(--ds-glass-light, rgba(255,255,255,0.055))',
    animation: 'fadeSlideIn 0.2s ease-out',
    backdropFilter: 'blur(14px) saturate(1.4)',
    WebkitBackdropFilter: 'blur(14px) saturate(1.4)',
  };

  const renderPin = (pin) => (
    <div key={pin.id} style={{
      padding: '10px 12px', marginBottom: '6px',
      background: 'var(--ds-surface-2, #1d1013)',
      border: '1px solid var(--ds-line-soft, #2a161b)',
      borderRadius: 'var(--ds-r-md, 14px)', position: 'relative',
    }}>
      {/* Sender + date/time */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '5px', gap: '6px' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--ds-gold, #c4a882)', flexShrink: 0 }}>
          {pin.message_sender && pin.message_sender !== 'Unknown' ? pin.message_sender : '—'}
        </span>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: '10px', color: 'var(--ds-ink-low, #9c8983)' }}>{timeAgo(pin.pinned_at)}</div>
          <div style={{ fontSize: '9px', color: 'var(--ds-ink-faint, #6e5a54)', whiteSpace: 'nowrap' }}>{formatPinDate(pin.pinned_at)}</div>
        </div>
      </div>

      {/* Message snapshot */}
      <div style={{
        fontSize: '13px', color: 'var(--ds-ink-hi, #f1e7e3)',
        lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        userSelect: 'text', WebkitUserSelect: 'text',
        borderLeft: '2px solid var(--ds-gold-a4c, rgba(196,168,130,0.3))',
        paddingLeft: '8px',
        marginBottom: '6px',
      }}>
        {truncate(pin.message_content)}
      </div>

      {/* Note */}
      {pin.note && (
        <div style={{ marginTop: '5px', fontSize: '11px', color: 'var(--ds-ink-low, #9c8983)', fontStyle: 'italic', borderTop: '1px solid var(--ds-line-soft, #2a161b)', paddingTop: '4px' }}>
          📝 {pin.note}
        </div>
      )}

      {/* Actions row */}
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Jump to message */}
        {onJumpToMessage && (
          <button
            onClick={() => { onJumpToMessage(pin.message_id); }}
            style={{ fontSize: '10px', color: '#7aa2f7', background: 'rgba(122,162,247,0.1)', border: '1px solid rgba(122,162,247,0.25)', borderRadius: 'var(--ds-r-sm, 10px)', padding: '2px 7px', cursor: 'pointer', minHeight: '32px', minWidth: '44px', display: 'flex', alignItems: 'center' }}
            title="Jump to message"
          >↗ Go to</button>
        )}

        {/* Move to category */}
        {categories.length > 0 && (
          <select
            value={pin.category_id || ''}
            onChange={e => moveToCategory(pin.id, e.target.value || null)}
            style={{ fontSize: '10px', background: 'var(--ds-surface-2, #1d1013)', color: 'var(--ds-ink-low, #9c8983)', border: '1px solid var(--ds-line-soft, #2a161b)', borderRadius: 'var(--ds-r-sm, 10px)', padding: '2px 4px', cursor: 'pointer', minHeight: '32px' }}
          >
            <option value="">No category</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}

        {/* Remove pin */}
        {removingPinId === pin.id ? (
          <>
            <button onClick={() => removePin(pin.id)} style={{ fontSize: '10px', color: '#fff', background: 'var(--ds-danger, #d6485e)', border: 'none', borderRadius: 'var(--ds-r-sm, 10px)', padding: '3px 9px', cursor: 'pointer', fontWeight: 600, minHeight: '32px' }}>✓ Confirm unpin</button>
            <button onClick={() => setRemovingPinId(null)} style={{ fontSize: '10px', color: 'var(--ds-ink-low, #9c8983)', background: 'none', border: '1px solid var(--ds-line-soft, #2a161b)', borderRadius: 'var(--ds-r-sm, 10px)', padding: '3px 7px', cursor: 'pointer', minHeight: '32px' }}>Cancel</button>
          </>
        ) : (
          <button onClick={() => setRemovingPinId(pin.id)} style={{ fontSize: '10px', color: 'var(--ds-danger, #d6485e)', background: 'none', border: '1px solid var(--ds-danger-a22, rgba(214,72,94,0.15))', borderRadius: 'var(--ds-r-sm, 10px)', padding: '2px 7px', cursor: 'pointer', marginLeft: 'auto', minHeight: '32px', minWidth: '44px', display: 'flex', alignItems: 'center' }}>📌 Unpin</button>
        )}
      </div>
    </div>
  );

  const renderCategory = (cat, catPins) => {
    const isOpen = !collapsed[cat.id];
    return (
      <div key={cat.id} style={{ marginBottom: '12px' }}>
        {/* Category header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 4px', cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setCollapsed(prev => ({ ...prev, [cat.id]: !prev[cat.id] }))}>
          <span style={{ color: cat.color || 'var(--ds-gold, #c4a882)', fontSize: '14px', lineHeight: '1' }}>{isOpen ? '▾' : '▸'}</span>
          {editingCatId === cat.id ? (
            <input
              value={editingCatName}
              onChange={e => setEditingCatName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') renameCategory(cat.id); if (e.key === 'Escape') setEditingCatId(null); }}
              onClick={e => e.stopPropagation()}
              autoFocus
              style={{ background: 'var(--ds-surface-2, #1d1013)', color: 'var(--ds-ink-hi, #f1e7e3)', border: `1px solid ${cat.color || 'var(--ds-gold, #c4a882)'}`, borderRadius: 'var(--ds-r-sm, 10px)', padding: '2px 6px', fontSize: '12px', flex: 1 }}
            />
          ) : (
            <span style={{ fontWeight: 700, fontSize: '12px', color: cat.color || 'var(--ds-gold, #c4a882)', textTransform: 'uppercase', letterSpacing: '0.8px', flex: 1 }}>
              {cat.name} ({catPins.length})
            </span>
          )}
          <button
            onClick={e => { e.stopPropagation(); setEditingCatName(cat.name); setEditingCatId(cat.id); }}
            style={{ background: 'none', border: 'none', color: 'var(--ds-ink-faint, #6e5a54)', cursor: 'pointer', fontSize: '12px', padding: '0 3px', minHeight: '32px', minWidth: '32px' }}
            title="Rename"
          >✏️</button>
          <button
            onClick={e => { e.stopPropagation(); deleteCategory(cat.id); }}
            style={{ background: 'none', border: 'none', color: 'var(--ds-danger-a44, rgba(214,72,94,0.27))', cursor: 'pointer', fontSize: '12px', padding: '0 3px', minHeight: '32px', minWidth: '32px' }}
            title="Delete category"
          >🗑️</button>
        </div>

        {isOpen && (
          <div style={{ paddingLeft: '4px' }}>
            {catPins.length === 0
              ? <div style={{ fontSize: '11px', color: 'var(--ds-ink-faint, #6e5a54)', padding: '6px 8px', fontStyle: 'italic' }}>No pins in this category</div>
              : catPins.map(renderPin)
            }
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={panelStyle}>
      {/* Header */}
      <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid var(--ds-line-soft, #2a161b)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div>
          <div style={{ fontFamily: "'Cinzel', serif", fontSize: '14px', fontWeight: 700, color: 'var(--ds-ink-hi, #f1e7e3)', letterSpacing: '0.05em' }}>
            📌 Pinned Messages
          </div>
          <div style={{ fontSize: '11px', color: 'var(--ds-ink-faint, #6e5a54)', marginTop: '2px' }}>{pins.length} pin{pins.length !== 1 ? 's' : ''}</div>
        </div>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', color: 'var(--ds-ink-low, #9c8983)', cursor: 'pointer', fontSize: '20px', padding: '4px 8px', lineHeight: 1, minHeight: '40px', minWidth: '40px' }}
        >✕</button>
      </div>

      {/* A failed organize used to be invisible — the whole point of SW-10 is
          that you find out, so this has to be on screen, not in state. */}
      {pinError && (
        <div style={{ margin: '10px 14px 0', padding: '8px 10px', borderRadius: '8px', background: 'rgba(198,40,40,0.12)', border: '1px solid rgba(198,40,40,0.32)', color: '#ffb3ab', fontSize: '11.5px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          <span style={{ flex: 1 }}>{pinError}</span>
          <button onClick={() => setPinError('')} aria-label="Dismiss" style={{ background: 'none', border: 'none', color: '#ffb3ab', cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>
      )}

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
        {loading ? (
          <div style={{ color: 'var(--ds-ink-faint, #6e5a54)', fontSize: '13px', textAlign: 'center', padding: '24px' }}>Loading…</div>
        ) : pins.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--ds-ink-faint, #6e5a54)' }}>
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>📌</div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--ds-ink, #cdbbb4)', marginBottom: '6px' }}>No pins yet</div>
            <div style={{ fontSize: '12px', color: 'var(--ds-ink-faint, #6e5a54)' }}>Long-press any message and tap 📌 to pin it here.</div>
          </div>
        ) : (
          <>
            {/* Categorized pins */}
            {categories.map(cat => {
              const catPins = pinsByCategory[cat.id] || [];
              return renderCategory(cat, catPins);
            })}

            {/* Uncategorized */}
            {uncategorized.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 4px', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setCollapsed(prev => ({ ...prev, '__uncategorized__': !prev['__uncategorized__'] }))}>
                  <span style={{ color: 'var(--ds-ink-low, #9c8983)', fontSize: '14px', lineHeight: '1' }}>{!collapsed['__uncategorized__'] ? '▾' : '▸'}</span>
                  <span style={{ fontWeight: 700, fontSize: '12px', color: 'var(--ds-ink, #cdbbb4)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    Uncategorized ({uncategorized.length})
                  </span>
                </div>
                {!collapsed['__uncategorized__'] && (
                  <div style={{ paddingLeft: '4px' }}>
                    {uncategorized.map(renderPin)}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer — Add Category */}
      <div style={{ padding: '12px 14px', borderTop: '1px solid var(--ds-line-soft, #2a161b)', flexShrink: 0 }}>
        {creatingCat ? (
          <div>
            <input
              value={newCatName}
              onChange={e => setNewCatName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') createCategory(); if (e.key === 'Escape') setCreatingCat(false); }}
              placeholder="Category name…"
              autoFocus
              style={{ background: 'var(--ds-surface-2, #1d1013)', color: 'var(--ds-ink-hi, #f1e7e3)', border: '1px solid var(--ds-line, #3a2128)', borderRadius: 'var(--ds-r-md, 14px)', padding: '7px 10px', fontSize: '13px', width: '100%', boxSizing: 'border-box', marginBottom: '8px', outline: 'none' }}
            />
            {/* Color picker */}
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginBottom: '8px' }}>
              {CAT_COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setNewCatColor(c)}
                  style={{ width: '20px', height: '20px', borderRadius: '50%', background: c, border: newCatColor === c ? '2px solid #fff' : '2px solid transparent', cursor: 'pointer', padding: 0 }}
                />
              ))}
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={createCategory} style={{ flex: 1, background: 'rgba(209,179,255,0.15)', border: '1px solid rgba(209,179,255,0.3)', borderRadius: '6px', color: '#d1b3ff', fontSize: '12px', padding: '7px', cursor: 'pointer', fontFamily: "'Cinzel', serif", letterSpacing: '0.5px' }}>
                Create
              </button>
              <button onClick={() => setCreatingCat(false)} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: t.textMuted, fontSize: '12px', padding: '7px 12px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setCreatingCat(true)}
            style={{ width: '100%', background: 'none', border: '1px dashed var(--ds-accent-a22, rgba(198,40,40,0.14))', borderRadius: 'var(--ds-r-md, 14px)', color: 'var(--ds-accent-a66, rgba(198,40,40,0.4))', fontSize: '12px', padding: '8px', cursor: 'pointer', fontFamily: "'Cinzel', serif", letterSpacing: '0.5px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            + Add Category
          </button>
        )}
      </div>
    </div>
  );
}

// ─── PIN POPOVER ──────────────────────────────────────────────────────────────
// Small popover that opens from the action bar pin button.
// Lets the user pick a category (or none) and saves the pin.
export function PinPopover({ message, contextType, contextId, userId, onClose, onPinned, anchorEl }) {
  const { theme: _t } = useContext(ThemeContext);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [creatingCat, setCreatingCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState(CAT_COLORS[0]);
  const popRef = useRef(null);
  const [pos, setPos] = useState({ top: -9999, left: -9999 });

  useEffect(() => {
    supabase
      .from('pin_categories')
      .select('*')
      .eq('created_by', userId)
      .order('sort_order')
      .then(({ data }) => setCategories(data || []));
  }, [userId]);

  // Compute fixed position from anchor element
  useLayoutEffect(() => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const popH = popRef.current?.offsetHeight || 320;
    const popW = 220;
    const margin = 6;
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;

    let top, left;
    if (spaceAbove >= popH + margin || spaceAbove > spaceBelow) {
      // Open upward
      top = rect.top - popH - margin;
    } else {
      // Open downward
      top = rect.bottom + margin;
    }
    left = Math.min(rect.right - popW, window.innerWidth - popW - 8);
    left = Math.max(8, left);
    setPos({ top, left });
  }, [anchorEl, categories]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (popRef.current && !popRef.current.contains(e.target) &&
          anchorEl && !anchorEl.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose, anchorEl]);

  const savePin = async (catId) => {
    setSaving(true);
    const contextCol = contextType === 'convo' ? 'convo_id'
      : contextType === 'channel' ? 'channel_id'
      : 'story_id';

    const sourceTable = contextType === 'convo' ? 'dm_messages'
      : contextType === 'channel' ? 'channel_messages'
      : 'story_messages';

    // Extract sender name from multiple possible message formats:
    // - ChannelView/DMView: msg.senderProfile.display_name (the enriched profile)
    // - SixGuysRP: msg.name
    // - DM: msg.sender_type === 'user' handled via senderProfile
    const senderName =
      message.senderProfile?.display_name ||
      message.sender_name ||
      message.display_name ||
      message.name ||
      (message.is_ai_character ? (message.sender_name || 'AI Character') : null) ||
      'Unknown';

    const { error } = await supabase.from('pinned_messages').upsert({
      pinned_by: userId,
      category_id: catId || null,
      [contextCol]: contextId,
      source_table: sourceTable,
      message_id: String(message.id),
      message_content: message.content || message.text || '',
      message_sender: senderName,
      sender_avatar: message.avatar_url || message.senderProfile?.avatar_url || null,
    }, { onConflict: 'pinned_by,message_id' });

    setSaving(false);
    if (!error) {
      onPinned?.();
      onClose();
    }
  };

  const createAndPin = async () => {
    const name = newCatName.trim();
    if (!name) return;
    const { data } = await supabase
      .from('pin_categories')
      .insert({ created_by: userId, name, color: newCatColor, sort_order: categories.length })
      .select().single();
    if (data) {
      setCategories(prev => [...prev, data]);
      setCreatingCat(false);
      setNewCatName('');
      await savePin(data.id);
    }
  };

  const popoverContent = (
    <div ref={popRef} style={{
      position: 'fixed', zIndex: 9999,
      top: pos.top, left: pos.left,
      background: 'var(--ds-glass, rgba(29, 16, 19, 0.77))',
      border: '1px solid var(--ds-glass-edge, #c4a8823d)',
      borderRadius: 'var(--ds-r-lg, 20px)', padding: '12px', width: '220px',
      boxShadow: 'var(--ds-shadow-2), inset 0 1px 0 var(--ds-glass-light, rgba(255,255,255,0.055))',
      animation: 'fadeSlideIn 0.15s ease-out',
      backdropFilter: 'blur(14px) saturate(1.4)',
      WebkitBackdropFilter: 'blur(14px) saturate(1.4)',
    }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--ds-ink-hi, #f1e7e3)', marginBottom: '10px', fontFamily: "'Cinzel', serif", letterSpacing: '0.5px' }}>
        📌 Pin to…
      </div>

      {/* No category */}
      <button
        onClick={() => savePin(null)}
        disabled={saving}
        style={{ width: '100%', textAlign: 'left', background: 'var(--ds-surface-2, #1d1013)', border: '1px solid var(--ds-line-soft, #2a161b)', borderRadius: 'var(--ds-r-md, 14px)', color: 'var(--ds-ink, #cdbbb4)', fontSize: '12px', padding: '7px 10px', cursor: 'pointer', marginBottom: '4px', minHeight: '36px', display: 'flex', alignItems: 'center' }}
      >
        No category
      </button>

      {/* Existing categories */}
      {categories.map(cat => (
        <button
          key={cat.id}
          onClick={() => savePin(cat.id)}
          disabled={saving}
          style={{ width: '100%', textAlign: 'left', background: 'var(--ds-surface-2, #1d1013)', border: `1px solid ${cat.color || 'var(--ds-gold, #c4a882)'}33`, borderRadius: 'var(--ds-r-md, 14px)', color: cat.color || 'var(--ds-gold, #c4a882)', fontSize: '12px', padding: '7px 10px', cursor: 'pointer', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px', minHeight: '36px' }}
        >
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: cat.color || 'var(--ds-gold, #c4a882)', flexShrink: 0 }} />
          {cat.name}
        </button>
      ))}

      <div style={{ height: '1px', background: 'var(--ds-line-soft, #2a161b)', margin: '8px 0' }} />

      {creatingCat ? (
        <div>
          <input
            value={newCatName}
            onChange={e => setNewCatName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') createAndPin(); if (e.key === 'Escape') setCreatingCat(false); }}
            placeholder="New category name…"
            autoFocus
            style={{ background: 'var(--ds-surface-2, #1d1013)', color: 'var(--ds-ink-hi, #f1e7e3)', border: '1px solid var(--ds-line, #3a2128)', borderRadius: 'var(--ds-r-md, 14px)', padding: '6px 8px', fontSize: '12px', width: '100%', boxSizing: 'border-box', marginBottom: '6px', outline: 'none' }}
          />
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '6px' }}>
            {CAT_COLORS.map(c => (
              <button key={c} onClick={() => setNewCatColor(c)}
                style={{ width: '16px', height: '16px', borderRadius: '50%', background: c, border: newCatColor === c ? '2px solid #fff' : '2px solid transparent', cursor: 'pointer', padding: 0 }} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button onClick={createAndPin} style={{ flex: 1, background: 'var(--ds-accent-a15, rgba(198,40,40,0.08))', border: '1px solid var(--ds-accent-a26, rgba(198,40,40,0.15))', borderRadius: 'var(--ds-r-md, 14px)', color: 'var(--ds-accent, #c62828)', fontSize: '11px', padding: '5px', cursor: 'pointer', minHeight: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Create & Pin</button>
            <button onClick={() => setCreatingCat(false)} style={{ background: 'none', border: '1px solid var(--ds-line-soft, #2a161b)', borderRadius: 'var(--ds-r-md, 14px)', color: 'var(--ds-ink-faint, #6e5a54)', fontSize: '11px', padding: '5px 8px', cursor: 'pointer', minHeight: '36px', minWidth: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setCreatingCat(true)}
          style={{ width: '100%', background: 'none', border: '1px dashed var(--ds-accent-a22, rgba(198,40,40,0.14))', borderRadius: 'var(--ds-r-md, 14px)', color: 'var(--ds-accent-a66, rgba(198,40,40,0.4))', fontSize: '11px', padding: '6px', cursor: 'pointer', minHeight: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          + New category
        </button>
      )}
    </div>
  );

  return ReactDOM.createPortal(popoverContent, document.body);
}
